import React, { useEffect, useMemo, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { X, Plus, Minus, Trash2, ShoppingBag, Lock, CheckCircle2, Loader2 } from "lucide-react";
import { useCart } from "../../context/CartContext";
import { BrandName } from "./BrandName";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const CONVERGE_SCRIPT = "https://api.convergepay.com/hosted-payments/PayWithConverge.js";
const TAX_RATE = 0.12;
const money = (n) => `$${n.toFixed(2)}`;
const TIPS = [["none", "No tip"], ["10", "10%"], ["15", "15%"], ["20", "20%"]];

const useConvergeScript = () => {
  const [ready, setReady] = useState(!!window.PayWithConverge);
  useEffect(() => {
    if (window.PayWithConverge) { setReady(true); return; }
    const s = document.createElement("script");
    s.src = CONVERGE_SCRIPT; s.async = true;
    s.onload = () => setReady(true);
    document.head.appendChild(s);
  }, []);
  return ready;
};

export const CheckoutDrawer = () => {
  const { open, closeCheckout, lines, count, subtotal, setQty, clear } = useCart();
  const scriptReady = useConvergeScript();
  const [tipType, setTipType] = useState("none");
  const [customTip, setCustomTip] = useState("");
  const [cust, setCust] = useState({ firstName: "", lastName: "", phone: "", email: "", pickupTime: "", notes: "" });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [done, setDone] = useState(null); // {order_number, method}
  const [fallback, setFallback] = useState(false); // online payment down -> offer pay at pickup
  const [orderingOpen, setOrderingOpen] = useState(true); // business hours gate

  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    if (!open) { setDone(null); setFallback(false); setError(""); }
  }, [open]);

  useEffect(() => {
    if (!open) return;
    fetch(`${API}/ordering-status`).then((r) => r.json()).then((d) => setOrderingOpen(!!d.open)).catch(() => {});
  }, [open]);

  const tip = useMemo(() => {
    if (tipType === "custom") return Math.max(0, parseFloat(customTip) || 0);
    if (tipType === "none") return 0;
    return subtotal * (parseInt(tipType, 10) / 100);
  }, [tipType, customTip, subtotal]);
  const tax = subtotal * TAX_RATE;
  const total = subtotal + tax + tip;

  const setField = (k) => (e) => setCust((c) => ({ ...c, [k]: e.target.value }));

  const pay = async () => {
    setError("");
    if (count === 0) { setError("Your cart is empty."); return; }
    if (!cust.firstName.trim() || !cust.phone.trim()) { setError("Please enter your name and phone number."); return; }
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(cust.email.trim())) { setError("Please enter a valid email address for your receipt."); return; }
    if (!scriptReady || !window.PayWithConverge) { setError("Payment is still loading, try again in a moment."); return; }
    setBusy(true);
    try {
      const res = await fetch(`${API}/checkout/create-token`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          customer: cust,
          items: lines.map((l) => ({ id: l.id, qty: l.qty })),
          tipType, customTip: tipType === "custom" ? String(tip) : null,
        }),
      });
      const data = await res.json();
      if (!res.ok) {
        if (res.status === 503) { setFallback(true); setBusy(false); return; }
        throw new Error(data.detail || "Could not start payment.");
      }
      const { order_id, ssl_txn_auth_token } = data;

      window.PayWithConverge.open(
        { ssl_txn_auth_token },
        {
          onApproval: async (response) => {
            try {
              const cr = await fetch(`${API}/checkout/confirm`, {
                method: "POST", headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ order_id, response }),
              });
              const cd = await cr.json();
              if (cr.ok && cd.status === "paid") {
                setDone({ order_number: cd.order_number });
                clear();
              } else {
                setError("Payment could not be verified. Please contact us before re-trying.");
              }
            } catch {
              setError("Payment verification failed. Please contact us.");
            } finally { setBusy(false); }
          },
          onDeclined: (r) => { setError(`Payment declined: ${r?.ssl_result_message || "declined"}`); setBusy(false); },
          onError: () => { setError("Payment error. Please try again."); setBusy(false); },
          onCancelled: () => { setBusy(false); },
        }
      );
    } catch (e) {
      setError(e.message || "Something went wrong.");
      setBusy(false);
    }
  };

  const placePickupOrder = async () => {
    setError("");
    if (count === 0) { setError("Your cart is empty."); return; }
    if (!cust.firstName.trim() || !cust.phone.trim()) { setError("Please enter your name and phone number."); return; }
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(cust.email.trim())) { setError("Please enter a valid email address for your receipt."); return; }
    setBusy(true);
    try {
      const res = await fetch(`${API}/orders/pay-at-pickup`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          customer: cust,
          items: lines.map((l) => ({ id: l.id, qty: l.qty })),
          tipType, customTip: tipType === "custom" ? String(tip) : null,
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || "Could not place order.");
      setDone({ order_number: data.order_number, method: "pickup" });
      clear();
    } catch (e) {
      setError(e.message || "Something went wrong.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div className="fixed inset-0 z-[120] flex justify-end" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
          <motion.div className="absolute inset-0 bg-black/80 backdrop-blur-sm" onClick={closeCheckout} />
          <motion.aside
            className="relative h-full w-full sm:max-w-md bg-surface border-l border-gold/20 flex flex-col"
            initial={{ x: "100%" }} animate={{ x: 0 }} exit={{ x: "100%" }}
            transition={{ type: "spring", damping: 30, stiffness: 300 }}
            data-testid="checkout-drawer"
          >
            <div className="flex items-center justify-between border-b border-white/10 px-5 py-4">
              <h3 className="font-display text-xl font-semibold flex items-center gap-2"><ShoppingBag size={20} className="text-gold" /> Your Order</h3>
              <button onClick={closeCheckout} aria-label="Close" data-testid="checkout-close" className="h-10 w-10 grid place-items-center rounded-full border border-white/10 text-cream/70 hover:text-cream hover:border-gold/40 transition-colors"><X size={18} /></button>
            </div>

            {done ? (
              <div className="flex flex-1 flex-col items-center justify-center p-8 text-center" data-testid="checkout-success">
                <CheckCircle2 size={64} className="text-gold" />
                <h4 className="mt-5 font-display text-2xl font-semibold">Order confirmed! 🔥</h4>
                {done.method === "pickup" ? (
                  <>
                    <p className="mt-2 text-cream/60">Your order <span className="text-gold font-semibold">{done.order_number}</span> is sent to our kitchen. We'll have it ready for pickup shortly.</p>
                    <p className="mt-4 rounded-xl border border-gold/30 bg-gold/10 px-4 py-2.5 text-sm text-gold" data-testid="pickup-pay-note">Please pay at the counter when you pick up — cash or card accepted.</p>
                  </>
                ) : (
                  <p className="mt-2 text-cream/60">Your order <span className="text-gold font-semibold">{done.order_number}</span> is paid and sent to our kitchen. We'll have it ready for pickup shortly.</p>
                )}
                <button onClick={() => { setDone(null); closeCheckout(); }} className="mt-7 rounded-full bg-gold px-7 py-3.5 font-semibold text-ink">Done</button>
              </div>
            ) : (
              <>
                <div className="flex-1 overflow-y-auto px-5 py-4">
                  {count === 0 ? (
                    <p className="py-16 text-center text-cream/50">Your cart is empty. Add some items from the menu!</p>
                  ) : (
                    <div className="space-y-1">
                      {lines.map((l) => (
                        <div key={l.id} className="flex items-center gap-3 border-b border-white/8 py-3" data-testid={`cart-line-${l.id}`}>
                          <div className="min-w-0 flex-1">
                            <p className="truncate text-sm font-semibold text-cream">{l.name}{l.label !== "Reg" && l.label !== "Each" ? <span className="text-cream/45"> · {l.label}</span> : null}</p>
                            <p className="text-xs text-gold">{money(l.price)} each</p>
                          </div>
                          <div className="flex items-center gap-2">
                            <button onClick={() => setQty(l.id, l.qty - 1)} aria-label="decrease" className="h-8 w-8 grid place-items-center rounded-full border border-white/12 hover:border-gold/40"><Minus size={14} /></button>
                            <span className="w-5 text-center text-sm font-semibold">{l.qty}</span>
                            <button onClick={() => setQty(l.id, l.qty + 1)} aria-label="increase" className="h-8 w-8 grid place-items-center rounded-full border border-white/12 hover:border-gold/40"><Plus size={14} /></button>
                          </div>
                          <span className="w-16 text-right text-sm font-semibold">{money(l.qty * l.price)}</span>
                        </div>
                      ))}
                    </div>
                  )}

                  {count > 0 && (
                    <>
                      <div className="mt-5">
                        <p className="text-xs uppercase tracking-wide text-cream/45 mb-2">Add a tip</p>
                        <div className="flex flex-wrap gap-2">
                          {TIPS.map(([v, label]) => (
                            <button key={v} onClick={() => setTipType(v)} data-testid={`tip-${v}`}
                              className={`rounded-full px-4 py-2 text-sm font-semibold transition-colors ${tipType === v ? "bg-gold text-ink" : "border border-white/12 text-cream/70 hover:border-gold/40"}`}>{label}</button>
                          ))}
                          <button onClick={() => setTipType("custom")} data-testid="tip-custom" className={`rounded-full px-4 py-2 text-sm font-semibold transition-colors ${tipType === "custom" ? "bg-gold text-ink" : "border border-white/12 text-cream/70 hover:border-gold/40"}`}>Custom</button>
                        </div>
                        {tipType === "custom" && (
                          <input value={customTip} onChange={(e) => setCustomTip(e.target.value)} inputMode="decimal" placeholder="Tip amount ($)"
                            className="mt-2 w-full rounded-xl border border-white/12 bg-white/5 px-3 py-2.5 text-sm text-cream outline-none focus:border-gold/50" />
                        )}
                      </div>

                      <div className="mt-6 grid gap-3">
                        <p className="text-xs uppercase tracking-wide text-cream/45">Pickup details</p>
                        <div className="grid grid-cols-2 gap-3">
                          <input value={cust.firstName} onChange={setField("firstName")} placeholder="First name *" data-testid="checkout-firstname" className="rounded-xl border border-white/12 bg-white/5 px-3 py-2.5 text-sm text-cream outline-none focus:border-gold/50" />
                          <input value={cust.lastName} onChange={setField("lastName")} placeholder="Last name" className="rounded-xl border border-white/12 bg-white/5 px-3 py-2.5 text-sm text-cream outline-none focus:border-gold/50" />
                        </div>
                        <input value={cust.phone} onChange={setField("phone")} placeholder="Phone number *" data-testid="checkout-phone" inputMode="tel" className="rounded-xl border border-white/12 bg-white/5 px-3 py-2.5 text-sm text-cream outline-none focus:border-gold/50" />
                        <input value={cust.email} onChange={setField("email")} placeholder="Email (for your receipt) *" type="email" data-testid="checkout-email" className="rounded-xl border border-white/12 bg-white/5 px-3 py-2.5 text-sm text-cream outline-none focus:border-gold/50" />
                        <input value={cust.pickupTime} onChange={setField("pickupTime")} placeholder="Preferred pickup time (e.g. ASAP, 7:30 PM)" data-testid="checkout-pickup" className="rounded-xl border border-white/12 bg-white/5 px-3 py-2.5 text-sm text-cream outline-none focus:border-gold/50" />
                        <textarea value={cust.notes} onChange={setField("notes")} placeholder="Notes (allergies, extra sauces...)" rows={2} className="rounded-xl border border-white/12 bg-white/5 px-3 py-2.5 text-sm text-cream outline-none focus:border-gold/50" />
                      </div>
                    </>
                  )}
                </div>

                {count > 0 && (
                  <div className="border-t border-white/10 px-5 py-4">
                    <div className="space-y-1 text-sm">
                      <div className="flex justify-between text-cream/60"><span>Subtotal</span><span>{money(subtotal)}</span></div>
                      <div className="flex justify-between text-cream/60"><span>Tax (GST+PST 12%)</span><span>{money(tax)}</span></div>
                      <div className="flex justify-between text-cream/60"><span>Tip</span><span>{money(tip)}</span></div>
                      <div className="flex justify-between pt-1 font-display text-lg font-semibold"><span>Total</span><span className="text-gold" data-testid="checkout-total">{money(total)}</span></div>
                    </div>
                    {!orderingOpen && (
                      <p className="mt-3 rounded-xl border border-red-400/30 bg-red-400/10 px-3 py-2.5 text-xs text-red-200" data-testid="ordering-closed-note">
                        We're closed right now — online ordering runs daily from 5:00 PM (Mon–Tue until midnight, Wed–Sun until 2:00 AM, Winnipeg time).
                      </p>
                    )}
                    {error && <p className="mt-3 rounded-xl bg-red-500/15 border border-red-500/30 px-3 py-2 text-xs text-red-300" data-testid="checkout-error">{error}</p>}
                    {fallback && (
                      <p className="mt-3 rounded-xl border border-amber-400/30 bg-amber-400/10 px-3 py-2.5 text-xs text-amber-200" data-testid="checkout-fallback-note">
                        Online card payment is temporarily unavailable. Place your order now and pay at the counter when you pick up.
                      </p>
                    )}
                    {fallback ? (
                      <>
                        <button onClick={placePickupOrder} disabled={busy || !orderingOpen} data-testid="checkout-pickup-pay"
                          className="mt-3 flex w-full items-center justify-center gap-2 rounded-full bg-gold px-6 py-4 text-base font-semibold text-ink transition-transform hover:-translate-y-0.5 disabled:opacity-60">
                          {busy ? <><Loader2 size={18} className="animate-spin" /> Placing order…</> : <>Place Order — Pay {money(total)} at Pickup</>}
                        </button>
                        <button onClick={pay} disabled={busy || !orderingOpen} data-testid="checkout-retry-card"
                          className="mt-2 w-full text-center text-xs text-cream/50 underline-offset-2 transition-colors hover:text-cream hover:underline">Try card payment again</button>
                      </>
                    ) : (
                      <button onClick={pay} disabled={busy || !orderingOpen} data-testid="checkout-pay"
                        className="mt-3 flex w-full items-center justify-center gap-2 rounded-full bg-gold px-6 py-4 text-base font-semibold text-ink transition-transform hover:-translate-y-0.5 disabled:opacity-60">
                        {busy ? <><Loader2 size={18} className="animate-spin" /> Processing…</> : <><Lock size={16} /> Pay {money(total)}</>}
                      </button>
                    )}
                    <p className="mt-2 flex items-center justify-center gap-1.5 text-[11px] text-cream/40"><Lock size={11} /> {fallback ? "Pay at the counter · cash or card accepted" : <>Secured by Elavon Converge · <BrandName className="text-[11px]" /></>}</p>
                  </div>
                )}
              </>
            )}
          </motion.aside>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default CheckoutDrawer;
